import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import AssistantService from '../services/assistantService'
import withApiError from '../services/axios/withApiError'

/*
 * BhanzuBot's state, kept in the store so the panel and its conversation
 * survive every route change (the widget is mounted once, in Layout).
 * Thunks reject with the API's own body (withApiError): `exception` says
 * what went wrong and a failed answer's body carries its messages.
 */

/** Ask a question: in the open conversation, or starting one. */
export const askBhanzuBot = createAsyncThunk(
    'assistant/ask',
    withApiError(({ conversationId, message, page }) =>
        conversationId
            ? AssistantService.send(conversationId, { message, page })
            : AssistantService.start({ message, page })
    )
)

/** Ask again for the answer to `questionId`, which failed. */
export const retryBhanzuBot = createAsyncThunk(
    'assistant/retry',
    withApiError(({ conversationId, questionId, page }) =>
        AssistantService.send(conversationId, { retryOf: questionId, page })
    )
)

/** Open one of the person's conversations (the remembered one, or from History). */
export const openBotConversation = createAsyncThunk(
    'assistant/openConversation',
    withApiError((conversationId) =>
        AssistantService.getConversation(conversationId)
    )
)

/** A page of past conversations; with a cursor, the next page. */
export const loadBotHistory = createAsyncThunk(
    'assistant/history',
    withApiError((cursor) => AssistantService.listConversations(cursor))
)

const NO_HISTORY = {
    items: [],
    nextCursor: null,
    loading: false,
    loaded: false,
    error: '',
}

const initialState = {
    open: false,
    // 'chat' or 'history' (the list of past conversations).
    view: 'chat',
    conversationId: null,
    conversation: null,
    messages: [],
    loadingConversation: false,
    // A question on its way: shown at once, replaced by the stored one.
    pending: false,
    pendingQuestion: '',
    // The composer's text, kept here so a prefill from the Guide and a
    // route change keep it; `focusToken` changes to focus the composer.
    draft: '',
    focusToken: 0,
    error: '',
    // The service is switched off (503 assistant-disabled): its message.
    disabled: '',
    history: NO_HISTORY,
}

/**
 * Add `incoming` messages to the conversation: one already there (the
 * question of a retry) is replaced in place, a new one is appended.
 */
const mergeMessages = (existing, incoming) => {
    const list = [...existing]
    ;(incoming ?? []).forEach((message) => {
        const at = list.findIndex((m) => m.id === message.id)
        if (at >= 0) list[at] = message
        else list.push(message)
    })
    return list
}

/** Take an answer's `{conversation, messages}` into the open conversation. */
const takeAnswer = (state, body) => {
    if (body?.conversation) {
        state.conversation = body.conversation
        state.conversationId = body.conversation.id
    }
    state.messages = mergeMessages(state.messages, body?.messages)
}

const GONE =
    'That conversation is no longer available. Your next message starts a new one.'

/**
 * A question that got no answer: show why and give the text back to the
 * composer, unless the answer failed after the question was stored (503
 * assistant-failed), when the question and its failed answer are shown
 * with a Retry instead.
 */
const askFailed = (state, action, question) => {
    state.pending = false
    state.pendingQuestion = ''
    const body = action.payload ?? {}
    if (body.exception === 'assistant-failed' && body.messages) {
        takeAnswer(state, body)
        return
    }
    if (question) state.draft = question
    if (body.exception === 'assistant-disabled') {
        state.disabled = body.message || 'BhanzuBot is switched off right now.'
        return
    }
    if (body.exception === 'conversation-not-found') {
        state.conversationId = null
        state.conversation = null
        state.messages = []
        state.error = GONE
        return
    }
    if (body.noResponse) {
        state.error =
            'BhanzuBot did not answer in time. Try again, or open History in a minute to see if the answer arrived.'
        return
    }
    state.error =
        (body.exception === 'invalid-request' && body.message) ||
        body.error ||
        body.message ||
        'BhanzuBot could not answer. Try again.'
}

const assistantSlice = createSlice({
    name: 'assistant',
    initialState,
    reducers: {
        /** Take what the browser remembered: the conversation and whether the panel was open. */
        restoreBot: (state, action) => {
            state.conversationId = action.payload?.conversationId ?? null
            state.open = !!action.payload?.open
        },
        openBot: (state) => {
            state.open = true
            state.focusToken += 1
        },
        minimiseBot: (state) => {
            state.open = false
        },
        /** Start over: the next question opens a new conversation. */
        newBotConversation: (state) => {
            state.conversationId = null
            state.conversation = null
            state.messages = []
            state.error = ''
            state.view = 'chat'
            state.focusToken += 1
        },
        showBotHistory: (state) => {
            state.view = 'history'
        },
        showBotChat: (state) => {
            state.view = 'chat'
        },
        setBotDraft: (state, action) => {
            state.draft = action.payload
        },
        /** Open the panel with the composer filled in and focused ("About Pipelines: "). */
        prefillBot: (state, action) => {
            state.open = true
            state.view = 'chat'
            state.draft = action.payload
            state.focusToken += 1
        },
        clearBotError: (state) => {
            state.error = ''
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(askBhanzuBot.pending, (state, action) => {
                state.pending = true
                state.pendingQuestion = action.meta.arg.message
                state.draft = ''
                state.error = ''
            })
            .addCase(askBhanzuBot.fulfilled, (state, action) => {
                state.pending = false
                state.pendingQuestion = ''
                takeAnswer(state, action.payload?.data)
            })
            .addCase(askBhanzuBot.rejected, (state, action) =>
                askFailed(state, action, action.meta.arg.message)
            )
            .addCase(retryBhanzuBot.pending, (state) => {
                state.pending = true
                state.error = ''
            })
            .addCase(retryBhanzuBot.fulfilled, (state, action) => {
                state.pending = false
                takeAnswer(state, action.payload?.data)
            })
            .addCase(retryBhanzuBot.rejected, (state, action) =>
                askFailed(state, action, '')
            )
            .addCase(openBotConversation.pending, (state, action) => {
                state.conversationId = action.meta.arg
                state.loadingConversation = true
                state.view = 'chat'
                state.error = ''
            })
            .addCase(openBotConversation.fulfilled, (state, action) => {
                // A late answer for a conversation already left is dropped.
                if (action.meta.arg !== state.conversationId) return
                state.loadingConversation = false
                state.conversation = action.payload?.data?.conversation ?? null
                state.messages = action.payload?.data?.messages ?? []
            })
            .addCase(openBotConversation.rejected, (state, action) => {
                if (action.meta.arg !== state.conversationId) return
                state.loadingConversation = false
                if (action.payload?.exception === 'conversation-not-found') {
                    state.conversationId = null
                    state.conversation = null
                    state.messages = []
                    return
                }
                state.error =
                    'This conversation could not be loaded. Try again.'
            })
            .addCase(loadBotHistory.pending, (state, action) => {
                state.history.loading = true
                state.history.error = ''
                if (!action.meta.arg) state.history.items = []
            })
            .addCase(loadBotHistory.fulfilled, (state, action) => {
                const data = action.payload?.data ?? {}
                state.history.loading = false
                state.history.loaded = true
                state.history.items = [
                    ...state.history.items,
                    ...(data.conversations ?? []),
                ]
                state.history.nextCursor = data.next_cursor ?? null
            })
            .addCase(loadBotHistory.rejected, (state, action) => {
                state.history.loading = false
                state.history.error =
                    action.payload?.message ||
                    action.payload?.error ||
                    'Your conversations could not be loaded.'
            })
    },
})

export const {
    restoreBot,
    openBot,
    minimiseBot,
    newBotConversation,
    showBotHistory,
    showBotChat,
    setBotDraft,
    prefillBot,
    clearBotError,
} = assistantSlice.actions

export default assistantSlice.reducer
